import React, {
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import {
  ChatEntryDto,
  ChatReferenceDto,
  RelatedContentDto,
  RelatedContentType
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { ELSIcon } from '@els/els-component-form-field-react';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import FlashcardsPanel from '../flashcards-panel/FlashcardsPanel.component';
import { QuizQuestionsPanel } from '../quiz-questions-panel/QuizQuestionsPanel.component';
import { QuizPanelMode } from '../quiz-questions-panel/quiz-questions-panel.constants';
import ReferenceVideoPanel from '../reference-video-panel/ReferenceVideoPanel.component';
import RelatedContentVideoPanel from '../related-content-video-panel/RelatedContentVideoPanel.component';
import RelatedContentImagePanel from '../related-content-image-panel/RelatedContentImagePanel.component';
import { AiChatEntryCitation } from '../../pages/ai-chat/AiChatEntryCitation.component';
import { getCitations } from '../../pages/ai-chat/ai-chat.utilities';

export type PanelProps = {
  chatReference?: ChatReferenceDto;
  relatedContent?: RelatedContentDto;
  selectedFlashcardEntryId?: number;
  selectedQuizQuestionEntryId?: number;
  selectedQuizQuestionMode?: QuizPanelMode;
  selectedCitationsEntryId?: number;
  selectedCitationIndex?: number;
  citationsEntry?: ChatEntryDto;
  onCitationClick?: (citation: ChatReferenceDto) => void;
  onClosePanel: () => void;
}

const Panel = (props: PanelProps): JSX.Element => {

  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Move focus to close button when panel opens
    if (closeBtnRef.current) {
      closeBtnRef.current.focus();
    }
  }, [
    props.chatReference,
    props.relatedContent,
    props.selectedFlashcardEntryId,
    props.selectedQuizQuestionEntryId,
    props.selectedQuizQuestionMode,
    props.selectedCitationsEntryId
  ]);

  useEffect(() => {
    // Scroll to the source row for the citation the user clicked, if any. The
    // row is rendered by AiChatEntryCitation with id `chat-source-{entryId}-{index}`.
    if (!props.selectedCitationsEntryId || props.selectedCitationIndex == null) {
      return;
    }
    const row = document.getElementById(`chat-source-${props.selectedCitationsEntryId}-${props.selectedCitationIndex}`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [props.selectedCitationsEntryId, props.selectedCitationIndex]);

  if (!props.chatReference && !props.relatedContent && !props.selectedFlashcardEntryId && !props.selectedQuizQuestionEntryId && !props.selectedCitationsEntryId) {
    return null;
  }

  const renderContent = (): ReactNode => {
    if (props.selectedQuizQuestionEntryId) {
      return (
        <QuizQuestionsPanel
          key={`${props.selectedQuizQuestionEntryId}-${props.selectedQuizQuestionMode}`}
          botEntryId={props.selectedQuizQuestionEntryId}
          mode={props.selectedQuizQuestionMode}
          onComplete={props.onClosePanel}
        />
      );
    }
    if (props.selectedFlashcardEntryId) {
      return <FlashcardsPanel key={props.selectedFlashcardEntryId} botEntryId={props.selectedFlashcardEntryId} />;
    }
    if (props.selectedCitationsEntryId && props.citationsEntry) {
      const citations = getCitations(props.citationsEntry) ?? [];
      return (
        <div key={props.selectedCitationsEntryId}>
          <h4 className="u-els-margin-bottom-2x">References</h4>
          <AiChatEntryCitation
            citations={citations}
            handleClick={props.onCitationClick ?? (() => undefined)}
          />
        </div>
      );
    }
    if (props.relatedContent) {
      if (props.relatedContent.contentType === RelatedContentType.OST) {
        return <RelatedContentVideoPanel key={props.relatedContent.id} relatedContent={props.relatedContent} />;
      }
      if (props.relatedContent.contentType === RelatedContentType.IM) {
        return <RelatedContentImagePanel key={props.relatedContent.id} relatedContent={props.relatedContent} />;
      }
    }
    if (props.chatReference) {
      return <ReferenceVideoPanel key={props.chatReference.id} chatReference={props.chatReference} />;
    }
    return null;
  };

  const getCloseButtonLabel = (): string => {
    if (props.selectedQuizQuestionEntryId) {
      return 'Close quiz questions';
    }
    if (props.selectedFlashcardEntryId) {
      return 'Close flashcards';
    }
    if (props.selectedCitationsEntryId) {
      return 'Close references';
    }
    if (props.relatedContent) {
      if (props.relatedContent.contentType === RelatedContentType.OST) {
        return 'Close video';
      }
      if (props.relatedContent.contentType === RelatedContentType.IM) {
        return 'Close image';
      }
    }
    if (props.chatReference) {
      return 'Close video';
    }
    return 'Close this modal window';
  };

  return (
    <div className="c-naa-panel">
      <div className="c-naa-panel__header">
        <FlexLayout modifiers={[FlexLayoutModifier.RIGHT]}>
          <FlexItem>
            <button
              ref={closeBtnRef}
              type="button"
              className="u-els-anchorize"
              onClick={() => {
                props.onClosePanel();
              }}
            >
              <ELSIcon name="close" size="1x" />
              <span className="u-els-hide-visually">{getCloseButtonLabel()}</span>
            </button>
          </FlexItem>
        </FlexLayout>
      </div>
      {renderContent()}
    </div>
  );
};

export default Panel;
